import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/JwtService";

// Bu uç noktalar 401 aldığında otomatik token yenileme yapılmaz:
// Login/RefreshToken döngüye girer; VerifyToken gövdesinde eski token çiftini taşır (verifyAuth kendisi yeniler).
const NO_AUTO_REFRESH = ["Auth/Login", "Auth/RefreshToken", "Auth/VerifyToken"];

// Aynı anda düşen birden çok istek için tek bir yenileme isteği yapılır
let refreshPromise = null;

const ApiService = {
  vueInstance: null,

  init(app) {

    this.vueInstance = app;
    this.vueInstance.use(VueAxios, axios);
    this.vueInstance.axios.defaults.baseURL = import.meta.env.VITE_API_URL;

    // Her istekte güncel token'ı kullan (başka bir sekme veya yenileme token'ı değiştirmiş olabilir)
    this.vueInstance.axios.interceptors.request.use((config) => {
      const token = JwtService.getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    // Erişim token'ı süresi dolunca (401) sessizce yenileyip isteği bir kez tekrarla (#37)
    this.vueInstance.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        const url = original?.url ?? "";
        if (
          error.response?.status !== 401 ||
          !original ||
          original._retried ||
          NO_AUTO_REFRESH.some((path) => url.includes(path))
        ) {
          return Promise.reject(error);
        }

        original._retried = true;
        const usedToken = (original.headers?.Authorization ?? "").replace("Bearer ", "");

        // Başka bir istek/sekme token'ı zaten yenilediyse doğrudan yeni token ile tekrar dene
        if (JwtService.getToken() && JwtService.getToken() !== usedToken) {
          return this.vueInstance.axios(original);
        }

        const refreshed = await this.refreshSession();
        if (!refreshed) {
          this.redirectToSignIn();
          return Promise.reject(error);
        }

        return this.vueInstance.axios(original);
      },
    );
  },

  /**
   * Refresh token ile yeni token çifti alır ve saklar. Başarılıysa true döner.
   * Eşzamanlı çağrılar aynı isteği paylaşır (refresh token tek kullanımlık olduğu için).
   */
  refreshSession() {
    if (refreshPromise) return refreshPromise;

    const token = JwtService.getToken();
    const refreshToken = JwtService.getRefreshToken();
    if (!token || !refreshToken) return Promise.resolve(false);

    refreshPromise = axios
      .post(`${import.meta.env.VITE_API_URL}Auth/RefreshToken`, { token, refreshToken })
      .then(({ data }) => {
        JwtService.saveToken(data.token.token, data.token.refreshToken);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });

    return refreshPromise;
  },

  redirectToSignIn() {
    JwtService.destroyToken();
    if (!window.location.hash.startsWith("#/auth/sign-in")) {
      window.location.hash = "#/auth/sign-in";
    }
  },

  setHeader() {
    this.vueInstance.axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${JwtService.getToken()}`;
    this.vueInstance.axios.defaults.headers.common["Accept"] = "application/json";
    this.vueInstance.axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
  },

  query(resource, params) {
    return this.vueInstance.axios.get(resource, params);
  },

  get(resource, slug = "", params) {
    const url = slug ? `${resource}/${slug}` : resource;
    return this.vueInstance.axios.get(url, { params: params });
  },

  post(resource, params) {
    return this.vueInstance.axios.post(`${resource}`, params);
  },

  patch(resource, params) {
    return this.vueInstance.axios.patch(`${resource}`, params);
  },

  update(resource, slug, params) {
    return this.vueInstance.axios.put(`${resource}/${slug}`, params);
  },

  put(resource, params) {
    return this.vueInstance.axios.put(`${resource}`, params);
  },

  delete(resource) {
    return this.vueInstance.axios.delete(resource);
  },
};

export default ApiService;
